/**
 * Centralized database of all instrument definitions and metadata.
 * 
 * This service provides access to the complete library of 30+ instruments
 * organized into categories (Lead, Bass, Pad, Arp, Percussion, SFX).
 * Each instrument includes metadata such as name, description, tags, icons,
 * default parameters, and preset configurations.
 * 
 * @example
 * ```typescript
 * import { instrumentDatabase } from './InstrumentDatabase';
 * 
 * // Get a specific instrument
 * const instrument = instrumentDatabase.getInstrument('square');
 * 
 * // Search for instruments
 * const results = instrumentDatabase.searchInstruments('bass');
 * 
 * // Get instruments by category
 * const leads = instrumentDatabase.getInstrumentsByCategory('Lead');
 * ```
 */

import { InstrumentType } from '../../types/composition';
import {
  InstrumentMetadata,
  InstrumentCategory,
  ParameterDefinition,
} from '../../types/instruments';

/**
 * InstrumentDatabase class - manages instrument metadata and provides search/filter capabilities.
 * 
 * This class is implemented as a singleton to ensure consistent access to instrument
 * definitions throughout the application. It initializes all 30+ instruments on construction
 * and provides methods for retrieval, searching, and filtering.
 * 
 * @class
 */
class InstrumentDatabase {
  private instruments: Map<InstrumentType, InstrumentMetadata>;

  constructor() {
    this.instruments = new Map();
    this.initializeInstruments();
  }

  /**
   * Initialize all instrument definitions
   */
  private initializeInstruments(): void {
    // Lead Instruments (8)
    this.addInstrument({
      id: 'square',
      name: 'square',
      displayName: 'Square Lead',
      category: 'Lead',
      description: 'Classic 8-bit square wave lead with bright, cutting tone perfect for melodies',
      tags: ['bright', 'cutting', 'classic', '8-bit', 'retro'],
      icon: '◼️',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 },
        oscillator: { type: 'square', pulseWidth: 0.5 },
        filter: { type: 'lowpass', frequency: 2000, resonance: 1 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('square'),
    });

    this.addInstrument({
      id: 'sawtooth',
      name: 'sawtooth',
      displayName: 'Saw Lead',
      category: 'Lead',
      description: 'Aggressive sawtooth lead with rich harmonics, ideal for powerful melodies',
      tags: ['aggressive', 'rich', 'powerful', 'harmonics'],
      icon: '🔺',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.15, sustain: 0.6, release: 0.15 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'lowpass', frequency: 3000, resonance: 2 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('sawtooth'),
    });

    this.addInstrument({
      id: 'pulse50',
      name: 'pulse50',
      displayName: 'Pulse Lead',
      category: 'Lead',
      description: 'Variable pulse width lead with warm, thick character',
      tags: ['warm', 'thick', 'pulse', 'variable'],
      icon: '▬',
      defaultParameters: {
        adsr: { attack: 0.02, decay: 0.1, sustain: 0.8, release: 0.1 },
        oscillator: { type: 'pulse', pulseWidth: 0.5 },
        filter: { type: 'lowpass', frequency: 2500, resonance: 1.5 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('pulse50'),
    });

    this.addInstrument({
      id: 'fmsynth',
      name: 'fmsynth',
      displayName: 'FM Lead',
      category: 'Lead',
      description: 'Frequency modulation lead with metallic, bell-like tones',
      tags: ['metallic', 'bell', 'fm', 'complex'],
      icon: '🔔',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.2, sustain: 0.5, release: 0.2 },
        oscillator: { harmonicity: 3, modulationIndex: 10 },
        filter: { type: 'lowpass', frequency: 4000, resonance: 1 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getFMParameters(),
      presets: this.createFMPresets('fmsynth'),
    });

    this.addInstrument({
      id: 'pulse25',
      name: 'pulse25',
      displayName: 'Chip Lead',
      category: 'Lead',
      description: 'Authentic NES-style lead with characteristic chiptune sound',
      tags: ['nes', 'chiptune', 'authentic', 'retro', 'classic'],
      icon: '🕹️',
      defaultParameters: {
        adsr: { attack: 0.005, decay: 0.1, sustain: 0.7, release: 0.05 },
        oscillator: { type: 'pulse', pulseWidth: 0.25 },
        filter: { type: 'lowpass', frequency: 3000, resonance: 0.5 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('pulse25'),
    });

    this.addInstrument({
      id: 'duosynth',
      name: 'duosynth',
      displayName: 'Arp Lead',
      category: 'Lead',
      description: 'Fast arpeggiating lead synth perfect for rhythmic patterns',
      tags: ['arpeggio', 'fast', 'rhythmic', 'pattern'],
      icon: '🎼',
      defaultParameters: {
        adsr: { attack: 0.005, decay: 0.05, sustain: 0.3, release: 0.05 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 3500, resonance: 2 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('duosynth'),
    });

    this.addInstrument({
      id: 'pulse75',
      name: 'pulse75',
      displayName: 'Bright Lead',
      category: 'Lead',
      description: 'High-passed square wave with piercing, bright character',
      tags: ['bright', 'piercing', 'high', 'cutting'],
      icon: '✨',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.08, sustain: 0.6, release: 0.08 },
        oscillator: { type: 'pulse', pulseWidth: 0.75 },
        filter: { type: 'highpass', frequency: 500, resonance: 1 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('pulse75'),
    });

    this.addInstrument({
      id: 'triangle',
      name: 'triangle',
      displayName: 'Soft Lead',
      category: 'Lead',
      description: 'Filtered triangle wave with mellow, smooth tone',
      tags: ['mellow', 'smooth', 'soft', 'gentle'],
      icon: '🔻',
      defaultParameters: {
        adsr: { attack: 0.03, decay: 0.1, sustain: 0.8, release: 0.15 },
        oscillator: { type: 'triangle' },
        filter: { type: 'lowpass', frequency: 1500, resonance: 0.5 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getLeadParameters(),
      presets: this.createLeadPresets('triangle'),
    });

    // Bass Instruments (6)
    this.addInstrument({
      id: 'amsynth',
      name: 'amsynth',
      displayName: 'Sub Bass',
      category: 'Bass',
      description: 'Deep sine wave bass with powerful sub frequencies',
      tags: ['deep', 'sub', 'powerful', 'low'],
      icon: '🌀',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.2, sustain: 0.7, release: 0.3 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 200, resonance: 0.5 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getBassParameters(),
      presets: this.createBassPresets('amsynth'),
    });

    this.addInstrument({
      id: 'drum-kick',
      name: 'drum-kick',
      displayName: 'Square Bass',
      category: 'Bass',
      description: 'Punchy square wave bass with aggressive attack',
      tags: ['punchy', 'aggressive', 'square', 'attack'],
      icon: '⬛',
      defaultParameters: {
        adsr: { attack: 0.005, decay: 0.15, sustain: 0.6, release: 0.2 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 300, resonance: 2 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getBassParameters(),
      presets: this.createBassPresets('drum-kick'),
    });

    this.addInstrument({
      id: 'membrane',
      name: 'membrane',
      displayName: 'Saw Bass',
      category: 'Bass',
      description: 'Aggressive sawtooth bass with rich low-end harmonics',
      tags: ['aggressive', 'rich', 'harmonics', 'powerful'],
      icon: '🔽',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.2, sustain: 0.5, release: 0.25 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'lowpass', frequency: 400, resonance: 3 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getBassParameters(),
      presets: this.createBassPresets('membrane'),
    });

    this.addInstrument({
      id: 'metal',
      name: 'metal',
      displayName: 'FM Bass',
      category: 'Bass',
      description: 'Modulated bass with movement and character',
      tags: ['modulated', 'movement', 'fm', 'dynamic'],
      icon: '🎚️',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.15, sustain: 0.6, release: 0.2 },
        oscillator: { harmonicity: 2, modulationIndex: 8 },
        filter: { type: 'lowpass', frequency: 350, resonance: 2 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getFMParameters(),
      presets: this.createFMPresets('metal'),
    });

    this.addInstrument({
      id: 'pluck',
      name: 'pluck',
      displayName: 'Pulse Bass',
      category: 'Bass',
      description: 'Thick pulse wave bass with warm low-end',
      tags: ['thick', 'warm', 'pulse', 'deep'],
      icon: '▪️',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.18, sustain: 0.7, release: 0.25 },
        oscillator: { type: 'pulse', pulseWidth: 0.3 },
        filter: { type: 'lowpass', frequency: 320, resonance: 2.5 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getBassParameters(),
      presets: this.createBassPresets('pluck'),
    });

    this.addInstrument({
      id: 'sampler',
      name: 'sampler',
      displayName: 'Wobble Bass',
      category: 'Bass',
      description: 'LFO-modulated bass with wobble effect',
      tags: ['wobble', 'lfo', 'modulated', 'dynamic'],
      icon: '🌊',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.1, sustain: 0.8, release: 0.2 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'lowpass', frequency: 500, resonance: 5, envelopeAmount: 0.8 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getBassParameters(),
      presets: this.createBassPresets('sampler'),
    });

    // Pad Instruments (5) - Using existing types creatively
    this.addInstrument({
      id: 'noise-pink',
      name: 'noise-pink',
      displayName: 'Soft Pad',
      category: 'Pad',
      description: 'Gentle layered pad with ambient, atmospheric quality',
      tags: ['gentle', 'ambient', 'atmospheric', 'soft'],
      icon: '☁️',
      defaultParameters: {
        adsr: { attack: 0.5, decay: 0.3, sustain: 0.8, release: 1.0 },
        oscillator: { type: 'triangle' },
        filter: { type: 'lowpass', frequency: 1000, resonance: 0.5 },
        effects: { chorus: true },
      },
      availableParameters: this.getPadParameters(),
      presets: this.createPadPresets('noise-pink'),
    });

    this.addInstrument({
      id: 'noise-white',
      name: 'noise-white',
      displayName: 'String Pad',
      category: 'Pad',
      description: 'String-like sustained pad with rich texture',
      tags: ['string', 'sustained', 'rich', 'texture'],
      icon: '🎻',
      defaultParameters: {
        adsr: { attack: 0.4, decay: 0.2, sustain: 0.9, release: 0.8 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'lowpass', frequency: 1200, resonance: 1 },
        effects: { chorus: true },
      },
      availableParameters: this.getPadParameters(),
      presets: this.createPadPresets('noise-white'),
    });

    // Percussion Instruments (10)
    this.addInstrument({
      id: 'drum-snare',
      name: 'drum-snare',
      displayName: 'Snare Drum',
      category: 'Percussion',
      description: 'Crisp snare with noise burst for classic drum sound',
      tags: ['crisp', 'snare', 'drum', 'percussion'],
      icon: '🥁',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.1, sustain: 0.0, release: 0.1 },
        oscillator: { type: 'triangle' },
        filter: { type: 'highpass', frequency: 200, resonance: 1 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-snare'),
    });

    this.addInstrument({
      id: 'drum-hat',
      name: 'drum-hat',
      displayName: 'Hi-Hat Closed',
      category: 'Percussion',
      description: 'Short metallic hi-hat with tight attack',
      tags: ['metallic', 'tight', 'hihat', 'percussion'],
      icon: '🎩',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.05, sustain: 0.0, release: 0.05 },
        oscillator: { type: 'square' },
        filter: { type: 'highpass', frequency: 5000, resonance: 1 },
        effects: { bitCrush: 2 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-hat'),
    });

    this.addInstrument({
      id: 'drum-tom',
      name: 'drum-tom',
      displayName: 'Tom Drum',
      category: 'Percussion',
      description: 'Resonant tom with tunable pitch',
      tags: ['resonant', 'tom', 'tunable', 'percussion'],
      icon: '🥁',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.15, sustain: 0.0, release: 0.15 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 400, resonance: 2 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-tom'),
    });

    this.addInstrument({
      id: 'drum-hat-open',
      name: 'drum-hat-open',
      displayName: 'Hi-Hat Open',
      category: 'Percussion',
      description: 'Sustained open hi-hat with long decay',
      tags: ['metallic', 'sustained', 'hihat', 'percussion'],
      icon: '🎩',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.2, sustain: 0.0, release: 0.2 },
        oscillator: { type: 'square' },
        filter: { type: 'highpass', frequency: 4000, resonance: 1 },
        effects: { bitCrush: 2 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-hat-open'),
    });

    this.addInstrument({
      id: 'drum-tom-low',
      name: 'drum-tom-low',
      displayName: 'Tom Low',
      category: 'Percussion',
      description: 'Low-pitched tom with deep resonance',
      tags: ['low', 'deep', 'tom', 'percussion'],
      icon: '🥁',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.2, sustain: 0.0, release: 0.2 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 200, resonance: 2 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-tom-low'),
    });

    this.addInstrument({
      id: 'drum-tom-mid',
      name: 'drum-tom-mid',
      displayName: 'Tom Mid',
      category: 'Percussion',
      description: 'Mid-pitched tom for fills and patterns',
      tags: ['mid', 'tom', 'fills', 'percussion'],
      icon: '🥁',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.15, sustain: 0.0, release: 0.15 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 400, resonance: 2 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-tom-mid'),
    });

    this.addInstrument({
      id: 'drum-tom-high',
      name: 'drum-tom-high',
      displayName: 'Tom High',
      category: 'Percussion',
      description: 'High-pitched tom with bright attack',
      tags: ['high', 'bright', 'tom', 'percussion'],
      icon: '🥁',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.1, sustain: 0.0, release: 0.1 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 800, resonance: 2 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-tom-high'),
    });

    this.addInstrument({
      id: 'drum-cymbal-crash',
      name: 'drum-cymbal-crash',
      displayName: 'Cymbal Crash',
      category: 'Percussion',
      description: 'Explosive crash cymbal for accents',
      tags: ['crash', 'cymbal', 'explosive', 'percussion'],
      icon: '💥',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.5, sustain: 0.0, release: 0.5 },
        oscillator: { type: 'square' },
        filter: { type: 'highpass', frequency: 3000, resonance: 0.5 },
        effects: { bitCrush: 2 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-cymbal-crash'),
    });

    this.addInstrument({
      id: 'drum-cymbal-ride',
      name: 'drum-cymbal-ride',
      displayName: 'Cymbal Ride',
      category: 'Percussion',
      description: 'Ride cymbal for rhythmic patterns',
      tags: ['ride', 'cymbal', 'rhythmic', 'percussion'],
      icon: '🔔',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.3, sustain: 0.0, release: 0.3 },
        oscillator: { type: 'square' },
        filter: { type: 'highpass', frequency: 4000, resonance: 1 },
        effects: { bitCrush: 2 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-cymbal-ride'),
    });

    this.addInstrument({
      id: 'drum-clap',
      name: 'drum-clap',
      displayName: 'Clap',
      category: 'Percussion',
      description: 'Hand clap sound for rhythmic accents',
      tags: ['clap', 'hand', 'rhythmic', 'percussion'],
      icon: '👏',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.08, sustain: 0.0, release: 0.08 },
        oscillator: { type: 'square' },
        filter: { type: 'bandpass', frequency: 1000, resonance: 2 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getPercussionParameters(),
      presets: this.createPercussionPresets('drum-clap'),
    });

    // Pad Instruments (additional 3)
    this.addInstrument({
      id: 'pad-choir',
      name: 'pad-choir',
      displayName: 'Choir Pad',
      category: 'Pad',
      description: 'Vocal-like pad texture with harmonic richness',
      tags: ['vocal', 'choir', 'harmonic', 'rich'],
      icon: '🎤',
      defaultParameters: {
        adsr: { attack: 0.6, decay: 0.3, sustain: 0.85, release: 1.2 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'bandpass', frequency: 1500, resonance: 1 },
        effects: { chorus: true },
      },
      availableParameters: this.getPadParameters(),
      presets: this.createPadPresets('pad-choir'),
    });

    this.addInstrument({
      id: 'pad-sweep',
      name: 'pad-sweep',
      displayName: 'Sweep Pad',
      category: 'Pad',
      description: 'Filter-swept evolving pad with movement',
      tags: ['sweep', 'evolving', 'movement', 'filter'],
      icon: '🌊',
      defaultParameters: {
        adsr: { attack: 0.7, decay: 0.4, sustain: 0.8, release: 1.5 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'lowpass', frequency: 800, resonance: 2, envelopeAmount: 0.7 },
        effects: { chorus: true },
      },
      availableParameters: this.getPadParameters(),
      presets: this.createPadPresets('pad-sweep'),
    });

    this.addInstrument({
      id: 'pad-drone',
      name: 'pad-drone',
      displayName: 'Drone Pad',
      category: 'Pad',
      description: 'Static harmonic drone for ambient textures',
      tags: ['drone', 'static', 'ambient', 'harmonic'],
      icon: '〰️',
      defaultParameters: {
        adsr: { attack: 1.0, decay: 0.5, sustain: 1.0, release: 2.0 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 600, resonance: 0.3 },
        effects: {},
      },
      availableParameters: this.getPadParameters(),
      presets: this.createPadPresets('pad-drone'),
    });

    // Arp Instruments (4)
    this.addInstrument({
      id: 'arp-fast',
      name: 'arp-fast',
      displayName: 'Fast Arp',
      category: 'Arp',
      description: 'Rapid 16th note arpeggios for energetic patterns',
      tags: ['fast', 'rapid', '16th', 'energetic'],
      icon: '⚡',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.03, sustain: 0.2, release: 0.03 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 4000, resonance: 2 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getArpParameters(),
      presets: this.createArpPresets('arp-fast'),
    });

    this.addInstrument({
      id: 'arp-slow',
      name: 'arp-slow',
      displayName: 'Slow Arp',
      category: 'Arp',
      description: 'Melodic 8th note patterns with sustain',
      tags: ['slow', 'melodic', '8th', 'sustained'],
      icon: '🎧',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.1, sustain: 0.6, release: 0.1 },
        oscillator: { type: 'triangle' },
        filter: { type: 'lowpass', frequency: 2500, resonance: 1 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getArpParameters(),
      presets: this.createArpPresets('arp-slow'),
    });

    this.addInstrument({
      id: 'arp-random',
      name: 'arp-random',
      displayName: 'Random Arp',
      category: 'Arp',
      description: 'Randomized note selection for unpredictable patterns',
      tags: ['random', 'unpredictable', 'chaotic', 'experimental'],
      icon: '🎲',
      defaultParameters: {
        adsr: { attack: 0.005, decay: 0.05, sustain: 0.3, release: 0.05 },
        oscillator: { type: 'pulse', pulseWidth: 0.3 },
        filter: { type: 'lowpass', frequency: 3000, resonance: 2.5 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getArpParameters(),
      presets: this.createArpPresets('arp-random'),
    });

    this.addInstrument({
      id: 'arp-chord',
      name: 'arp-chord',
      displayName: 'Chord Arp',
      category: 'Arp',
      description: 'Chord-based arpeggiation for harmonic patterns',
      tags: ['chord', 'harmonic', 'pattern', 'musical'],
      icon: '🎶',
      defaultParameters: {
        adsr: { attack: 0.01, decay: 0.08, sustain: 0.5, release: 0.08 },
        oscillator: { type: 'sawtooth' },
        filter: { type: 'lowpass', frequency: 2800, resonance: 1.5 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getArpParameters(),
      presets: this.createArpPresets('arp-chord'),
    });

    // Bass (additional 1)
    this.addInstrument({
      id: 'bass-sub',
      name: 'bass-sub',
      displayName: 'Sub Bass Deep',
      category: 'Bass',
      description: 'Ultra-deep sub bass with pure sine wave',
      tags: ['sub', 'deep', 'ultra', 'pure'],
      icon: '🎛️',
      defaultParameters: {
        adsr: { attack: 0.02, decay: 0.25, sustain: 0.8, release: 0.4 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 150, resonance: 0.3 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getBassParameters(),
      presets: this.createBassPresets('bass-sub'),
    });

    // SFX Instruments (7)
    this.addInstrument({
      id: 'sfx-laser',
      name: 'sfx-laser',
      displayName: 'Laser',
      category: 'SFX',
      description: 'Sci-fi laser sound with pitch sweep',
      tags: ['laser', 'scifi', 'sweep', 'zap'],
      icon: '🔫',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.15, sustain: 0.0, release: 0.15 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 2000, resonance: 5, envelopeAmount: 1 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-laser'),
    });

    this.addInstrument({
      id: 'sfx-explosion',
      name: 'sfx-explosion',
      displayName: 'Explosion',
      category: 'SFX',
      description: '8-bit explosion with noise burst',
      tags: ['explosion', 'boom', 'noise', 'impact'],
      icon: '💥',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.3, sustain: 0.0, release: 0.3 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 500, resonance: 1 },
        effects: { bitCrush: 2, distortion: 0.8 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-explosion'),
    });

    this.addInstrument({
      id: 'sfx-coin',
      name: 'sfx-coin',
      displayName: 'Coin',
      category: 'SFX',
      description: 'Classic coin pickup sound',
      tags: ['coin', 'pickup', 'collect', 'retro'],
      icon: '🪙',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.1, sustain: 0.0, release: 0.1 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 3000, resonance: 2 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-coin'),
    });

    this.addInstrument({
      id: 'sfx-jump',
      name: 'sfx-jump',
      displayName: 'Jump',
      category: 'SFX',
      description: 'Jump or bounce sound effect',
      tags: ['jump', 'bounce', 'hop', 'spring'],
      icon: '⬆️',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.12, sustain: 0.0, release: 0.12 },
        oscillator: { type: 'triangle' },
        filter: { type: 'lowpass', frequency: 1500, resonance: 1 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-jump'),
    });

    this.addInstrument({
      id: 'sfx-powerup',
      name: 'sfx-powerup',
      displayName: 'Power Up',
      category: 'SFX',
      description: 'Power-up ascending sound',
      tags: ['powerup', 'ascending', 'collect', 'upgrade'],
      icon: '⭐',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.25, sustain: 0.0, release: 0.25 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 4000, resonance: 3, envelopeAmount: 0.8 },
        effects: { bitCrush: 3 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-powerup'),
    });

    this.addInstrument({
      id: 'sfx-hit',
      name: 'sfx-hit',
      displayName: 'Hit',
      category: 'SFX',
      description: 'Impact or hit sound effect',
      tags: ['hit', 'impact', 'punch', 'strike'],
      icon: '👊',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.08, sustain: 0.0, release: 0.08 },
        oscillator: { type: 'square' },
        filter: { type: 'lowpass', frequency: 800, resonance: 2 },
        effects: { bitCrush: 2, distortion: 0.5 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-hit'),
    });

    this.addInstrument({
      id: 'sfx-blip',
      name: 'sfx-blip',
      displayName: 'Blip',
      category: 'SFX',
      description: 'Short notification blip sound',
      tags: ['blip', 'notification', 'beep', 'short'],
      icon: '🔔',
      defaultParameters: {
        adsr: { attack: 0.001, decay: 0.05, sustain: 0.0, release: 0.05 },
        oscillator: { type: 'sine' },
        filter: { type: 'lowpass', frequency: 2000, resonance: 1 },
        effects: { bitCrush: 4 },
      },
      availableParameters: this.getSFXParameters(),
      presets: this.createSFXPresets('sfx-blip'),
    });
  }

  /**
   * Add an instrument to the database
   */
  private addInstrument(metadata: InstrumentMetadata): void {
    this.instruments.set(metadata.id, metadata);
  }

  /**
   * Get parameter definitions for lead instruments
   */
  private getLeadParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.001,
        max: 2.0,
        default: 0.01,
        step: 0.001,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.001,
        max: 2.0,
        default: 0.1,
        step: 0.001,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'sustain',
        name: 'Sustain',
        type: 'slider',
        min: 0.0,
        max: 1.0,
        default: 0.7,
        step: 0.01,
        unit: '',
        description: 'Sustain level of the envelope',
      },
      {
        id: 'release',
        name: 'Release',
        type: 'slider',
        min: 0.001,
        max: 2.0,
        default: 0.1,
        step: 0.001,
        unit: 's',
        description: 'Release time of the envelope',
      },
      {
        id: 'filterFrequency',
        name: 'Filter Cutoff',
        type: 'knob',
        min: 100,
        max: 10000,
        default: 2000,
        step: 10,
        unit: 'Hz',
        description: 'Filter cutoff frequency',
      },
      {
        id: 'filterResonance',
        name: 'Resonance',
        type: 'knob',
        min: 0.1,
        max: 10,
        default: 1,
        step: 0.1,
        unit: '',
        description: 'Filter resonance amount',
      },
    ];
  }

  /**
   * Get parameter definitions for bass instruments
   */
  private getBassParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.001,
        max: 0.5,
        default: 0.01,
        step: 0.001,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.001,
        max: 1.0,
        default: 0.2,
        step: 0.001,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'sustain',
        name: 'Sustain',
        type: 'slider',
        min: 0.0,
        max: 1.0,
        default: 0.7,
        step: 0.01,
        unit: '',
        description: 'Sustain level of the envelope',
      },
      {
        id: 'release',
        name: 'Release',
        type: 'slider',
        min: 0.001,
        max: 1.0,
        default: 0.3,
        step: 0.001,
        unit: 's',
        description: 'Release time of the envelope',
      },
      {
        id: 'filterFrequency',
        name: 'Filter Cutoff',
        type: 'knob',
        min: 50,
        max: 1000,
        default: 300,
        step: 5,
        unit: 'Hz',
        description: 'Filter cutoff frequency',
      },
      {
        id: 'filterResonance',
        name: 'Resonance',
        type: 'knob',
        min: 0.1,
        max: 10,
        default: 2,
        step: 0.1,
        unit: '',
        description: 'Filter resonance amount',
      },
    ];
  }

  /**
   * Get parameter definitions for FM instruments
   */
  private getFMParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.001,
        max: 2.0,
        default: 0.01,
        step: 0.001,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.001,
        max: 2.0,
        default: 0.2,
        step: 0.001,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'sustain',
        name: 'Sustain',
        type: 'slider',
        min: 0.0,
        max: 1.0,
        default: 0.5,
        step: 0.01,
        unit: '',
        description: 'Sustain level of the envelope',
      },
      {
        id: 'release',
        name: 'Release',
        type: 'slider',
        min: 0.001,
        max: 2.0,
        default: 0.2,
        step: 0.001,
        unit: 's',
        description: 'Release time of the envelope',
      },
      {
        id: 'harmonicity',
        name: 'Harmonicity',
        type: 'knob',
        min: 0.5,
        max: 8,
        default: 3,
        step: 0.1,
        unit: '',
        description: 'FM harmonicity ratio',
      },
      {
        id: 'modulationIndex',
        name: 'Mod Index',
        type: 'knob',
        min: 0,
        max: 20,
        default: 10,
        step: 0.5,
        unit: '',
        description: 'FM modulation index',
      },
    ];
  }

  /**
   * Get parameter definitions for pad instruments
   */
  private getPadParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.1,
        max: 3.0,
        default: 0.5,
        step: 0.01,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.1,
        max: 2.0,
        default: 0.3,
        step: 0.01,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'sustain',
        name: 'Sustain',
        type: 'slider',
        min: 0.5,
        max: 1.0,
        default: 0.8,
        step: 0.01,
        unit: '',
        description: 'Sustain level of the envelope',
      },
      {
        id: 'release',
        name: 'Release',
        type: 'slider',
        min: 0.2,
        max: 3.0,
        default: 1.0,
        step: 0.01,
        unit: 's',
        description: 'Release time of the envelope',
      },
      {
        id: 'filterFrequency',
        name: 'Filter Cutoff',
        type: 'knob',
        min: 200,
        max: 5000,
        default: 1000,
        step: 10,
        unit: 'Hz',
        description: 'Filter cutoff frequency',
      },
    ];
  }

  /**
   * Get parameter definitions for percussion instruments
   */
  private getPercussionParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.001,
        max: 0.1,
        default: 0.001,
        step: 0.001,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.01,
        max: 0.5,
        default: 0.1,
        step: 0.01,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'pitch',
        name: 'Pitch',
        type: 'knob',
        min: -12,
        max: 12,
        default: 0,
        step: 1,
        unit: 'st',
        description: 'Pitch adjustment in semitones',
      },
    ];
  }

  /**
   * Get parameter definitions for arp instruments
   */
  private getArpParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.001,
        max: 0.5,
        default: 0.01,
        step: 0.001,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.001,
        max: 0.5,
        default: 0.05,
        step: 0.001,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'sustain',
        name: 'Sustain',
        type: 'slider',
        min: 0.0,
        max: 1.0,
        default: 0.3,
        step: 0.01,
        unit: '',
        description: 'Sustain level of the envelope',
      },
      {
        id: 'release',
        name: 'Release',
        type: 'slider',
        min: 0.001,
        max: 0.5,
        default: 0.05,
        step: 0.001,
        unit: 's',
        description: 'Release time of the envelope',
      },
      {
        id: 'filterFrequency',
        name: 'Filter Cutoff',
        type: 'knob',
        min: 500,
        max: 8000,
        default: 3000,
        step: 10,
        unit: 'Hz',
        description: 'Filter cutoff frequency',
      },
      {
        id: 'filterResonance',
        name: 'Resonance',
        type: 'knob',
        min: 0.1,
        max: 5,
        default: 2,
        step: 0.1,
        unit: '',
        description: 'Filter resonance amount',
      },
    ];
  }

  /**
   * Get parameter definitions for SFX instruments
   */
  private getSFXParameters(): ParameterDefinition[] {
    return [
      {
        id: 'attack',
        name: 'Attack',
        type: 'slider',
        min: 0.001,
        max: 0.2,
        default: 0.001,
        step: 0.001,
        unit: 's',
        description: 'Attack time of the envelope',
      },
      {
        id: 'decay',
        name: 'Decay',
        type: 'slider',
        min: 0.01,
        max: 1.0,
        default: 0.15,
        step: 0.01,
        unit: 's',
        description: 'Decay time of the envelope',
      },
      {
        id: 'pitch',
        name: 'Pitch',
        type: 'knob',
        min: -24,
        max: 24,
        default: 0,
        step: 1,
        unit: 'st',
        description: 'Pitch adjustment in semitones',
      },
      {
        id: 'filterFrequency',
        name: 'Filter Cutoff',
        type: 'knob',
        min: 100,
        max: 8000,
        default: 2000,
        step: 10,
        unit: 'Hz',
        description: 'Filter cutoff frequency',
      },
    ];
  }

  /**
   * Create preset configurations for lead instruments
   */
  private createLeadPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-bright`,
        instrumentId,
        name: 'Bright',
        description: 'Bright and cutting lead tone',
        parameters: {
          adsr: { attack: 0.005, decay: 0.08, sustain: 0.6, release: 0.08 },
          filter: { frequency: 3500, resonance: 2 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-warm`,
        instrumentId,
        name: 'Warm',
        description: 'Warm and smooth lead tone',
        parameters: {
          adsr: { attack: 0.03, decay: 0.15, sustain: 0.8, release: 0.15 },
          filter: { frequency: 1500, resonance: 0.5 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-punchy`,
        instrumentId,
        name: 'Punchy',
        description: 'Short and punchy lead tone',
        parameters: {
          adsr: { attack: 0.001, decay: 0.05, sustain: 0.3, release: 0.05 },
          filter: { frequency: 2500, resonance: 3 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Create preset configurations for bass instruments
   */
  private createBassPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-deep`,
        instrumentId,
        name: 'Deep',
        description: 'Deep and powerful bass',
        parameters: {
          adsr: { attack: 0.01, decay: 0.2, sustain: 0.8, release: 0.3 },
          filter: { frequency: 200, resonance: 1 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-punchy`,
        instrumentId,
        name: 'Punchy',
        description: 'Punchy and aggressive bass',
        parameters: {
          adsr: { attack: 0.005, decay: 0.1, sustain: 0.5, release: 0.15 },
          filter: { frequency: 400, resonance: 3 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-smooth`,
        instrumentId,
        name: 'Smooth',
        description: 'Smooth and sustained bass',
        parameters: {
          adsr: { attack: 0.02, decay: 0.25, sustain: 0.9, release: 0.4 },
          filter: { frequency: 250, resonance: 0.5 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Create preset configurations for FM instruments
   */
  private createFMPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-bell`,
        instrumentId,
        name: 'Bell',
        description: 'Bell-like metallic tone',
        parameters: {
          adsr: { attack: 0.001, decay: 0.3, sustain: 0.3, release: 0.5 },
          oscillator: { harmonicity: 4, modulationIndex: 12 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-electric`,
        instrumentId,
        name: 'Electric',
        description: 'Electric piano-like tone',
        parameters: {
          adsr: { attack: 0.005, decay: 0.2, sustain: 0.5, release: 0.3 },
          oscillator: { harmonicity: 2, modulationIndex: 8 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-metallic`,
        instrumentId,
        name: 'Metallic',
        description: 'Harsh metallic tone',
        parameters: {
          adsr: { attack: 0.01, decay: 0.15, sustain: 0.6, release: 0.2 },
          oscillator: { harmonicity: 6, modulationIndex: 15 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Create preset configurations for pad instruments
   */
  private createPadPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-soft`,
        instrumentId,
        name: 'Soft',
        description: 'Soft and gentle pad',
        parameters: {
          adsr: { attack: 0.8, decay: 0.3, sustain: 0.9, release: 1.5 },
          filter: { frequency: 800, resonance: 0.3 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-bright`,
        instrumentId,
        name: 'Bright',
        description: 'Bright and airy pad',
        parameters: {
          adsr: { attack: 0.5, decay: 0.2, sustain: 0.8, release: 1.0 },
          filter: { frequency: 2000, resonance: 1 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-dark`,
        instrumentId,
        name: 'Dark',
        description: 'Dark and moody pad',
        parameters: {
          adsr: { attack: 1.0, decay: 0.5, sustain: 0.9, release: 2.0 },
          filter: { frequency: 500, resonance: 0.5 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Create preset configurations for percussion instruments
   */
  private createPercussionPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-tight`,
        instrumentId,
        name: 'Tight',
        description: 'Tight and snappy',
        parameters: {
          adsr: { attack: 0.001, decay: 0.05, sustain: 0.0, release: 0.05 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-loose`,
        instrumentId,
        name: 'Loose',
        description: 'Loose and resonant',
        parameters: {
          adsr: { attack: 0.001, decay: 0.15, sustain: 0.0, release: 0.15 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-hard`,
        instrumentId,
        name: 'Hard',
        description: 'Hard hitting',
        parameters: {
          adsr: { attack: 0.001, decay: 0.08, sustain: 0.0, release: 0.08 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Create preset configurations for arp instruments
   */
  private createArpPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-sharp`,
        instrumentId,
        name: 'Sharp',
        description: 'Sharp and staccato',
        parameters: {
          adsr: { attack: 0.001, decay: 0.03, sustain: 0.1, release: 0.03 },
          filter: { frequency: 4000, resonance: 3 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-smooth`,
        instrumentId,
        name: 'Smooth',
        description: 'Smooth and flowing',
        parameters: {
          adsr: { attack: 0.02, decay: 0.1, sustain: 0.6, release: 0.1 },
          filter: { frequency: 2000, resonance: 1 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-bouncy`,
        instrumentId,
        name: 'Bouncy',
        description: 'Bouncy and playful',
        parameters: {
          adsr: { attack: 0.005, decay: 0.05, sustain: 0.3, release: 0.05 },
          filter: { frequency: 3000, resonance: 2 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Create preset configurations for SFX instruments
   */
  private createSFXPresets(instrumentId: InstrumentType): any[] {
    return [
      {
        id: `${instrumentId}-short`,
        instrumentId,
        name: 'Short',
        description: 'Short and punchy',
        parameters: {
          adsr: { attack: 0.001, decay: 0.08, sustain: 0.0, release: 0.08 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-long`,
        instrumentId,
        name: 'Long',
        description: 'Extended duration',
        parameters: {
          adsr: { attack: 0.001, decay: 0.25, sustain: 0.0, release: 0.25 },
        },
        isDefault: true,
        isUserCreated: false,
      },
      {
        id: `${instrumentId}-intense`,
        instrumentId,
        name: 'Intense',
        description: 'Intense and dramatic',
        parameters: {
          adsr: { attack: 0.001, decay: 0.15, sustain: 0.0, release: 0.15 },
          filter: { frequency: 1500, resonance: 5 },
        },
        isDefault: true,
        isUserCreated: false,
      },
    ];
  }

  /**
   * Get instrument metadata by ID.
   * 
   * @param id - The instrument type identifier (e.g., 'square', 'sawtooth', 'drum-kick')
   * @returns The instrument metadata if found, undefined otherwise
   * 
   * @example
   * ```typescript
   * const square = instrumentDatabase.getInstrument('square');
   * console.log(square.displayName); // "Square Lead"
   * ```
   */
  getInstrument(id: InstrumentType): InstrumentMetadata | undefined {
    return this.instruments.get(id);
  }

  /**
   * Get all available instruments.
   * 
   * @returns Array of all instrument metadata objects (30+ instruments)
   * 
   * @example
   * ```typescript
   * const allInstruments = instrumentDatabase.getAllInstruments();
   * console.log(`Total instruments: ${allInstruments.length}`);
   * ```
   */
  getAllInstruments(): InstrumentMetadata[] {
    return Array.from(this.instruments.values());
  }

  /**
   * Get all instruments in a specific category.
   * 
   * @param category - The instrument category to filter by
   * @returns Array of instruments matching the specified category
   * 
   * @example
   * ```typescript
   * const leads = instrumentDatabase.getInstrumentsByCategory('Lead');
   * const percussion = instrumentDatabase.getInstrumentsByCategory('Percussion');
   * ```
   */
  getInstrumentsByCategory(category: InstrumentCategory): InstrumentMetadata[] {
    return this.getAllInstruments().filter((inst) => inst.category === category);
  }

  /**
   * Search instruments by query string.
   * 
   * Searches across instrument display names, descriptions, tags, and categories.
   * The search is case-insensitive and returns all instruments if query is empty.
   * 
   * @param query - The search query string
   * @returns Array of instruments matching the search query
   * 
   * @example
   * ```typescript
   * // Search by name
   * const results = instrumentDatabase.searchInstruments('bass');
   * 
   * // Search by tag
   * const bright = instrumentDatabase.searchInstruments('bright');
   * 
   * // Empty query returns all instruments
   * const all = instrumentDatabase.searchInstruments('');
   * ```
   */
  searchInstruments(query: string): InstrumentMetadata[] {
    try {
      if (!query || query.trim() === '') {
        return this.getAllInstruments();
      }

      const lowerQuery = query.toLowerCase().trim();
      return this.getAllInstruments().filter((inst) => {
        try {
          return (
            inst.displayName.toLowerCase().includes(lowerQuery) ||
            inst.description.toLowerCase().includes(lowerQuery) ||
            inst.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)) ||
            inst.category.toLowerCase().includes(lowerQuery)
          );
        } catch (error) {
          console.warn(`Error filtering instrument ${inst.id}:`, error);
          return false;
        }
      });
    } catch (error) {
      console.error('Error searching instruments:', error);
      return this.getAllInstruments();
    }
  }

  /**
   * Get all available instrument categories.
   * 
   * @returns Array of all instrument category names
   * 
   * @example
   * ```typescript
   * const categories = instrumentDatabase.getCategories();
   * // ['Lead', 'Bass', 'Pad', 'Arp', 'Percussion', 'SFX']
   * ```
   */
  getCategories(): InstrumentCategory[] {
    return ['Lead', 'Bass', 'Pad', 'Arp', 'Percussion', 'SFX'];
  }
}

// Export singleton instance
export const instrumentDatabase = new InstrumentDatabase();
export default instrumentDatabase;
